import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IFilter, IGateway, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { LuminariasService } from '../../../luminarias/service';
import { GatewayService } from '../../../../../auxiliares/servicios/http/gateways.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { CrearEditarGatewayComponent } from '../crear-editar-gateway/crear-editar-gateway.component';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import { IEstadoCelda } from '../../../../../auxiliares/tabla/tabla.interfaces';

@Component({
  selector: 'app-listado-gateways',
  templateUrl: './listado-gateways.component.html',
  standalone: false,
})
export class ListadoGatewaysComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(GatewayService);
  private navigator = inject(Router);
  private formularios = inject(FormularioDialogService);

  public clientePropio = LoginService.getCliente();

  //// Tabla nueva
  public name = 'ListadoGatewaysComponent';
  public gateways?: IGateway[];
  public gateways$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IGateway>[];

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Administración - Crear gateways'),
    tooltip: 'Crear gateway',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),

    //Buscar
  };
  public search: IRegExpSearch = {
    fields: ['nombre', 'gatewayEui'],
  };

  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];

  // FILTROS
  // Cliente
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  public filtros: IFiltroTabla[] = [];

  //Query
  public filter: IFilter<IGateway> = {};
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'name',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  // Helper para verificar si una ubicación es válida
  private hasValidLocation(gateway: IGateway): boolean {
    return (
      gateway.ubicacion !== undefined &&
      gateway.ubicacion !== null &&
      gateway.ubicacion.coordinates &&
      gateway.ubicacion.coordinates.length === 2 &&
      !(
        gateway.ubicacion.coordinates[0] === 0 &&
        gateway.ubicacion.coordinates[1] === 0
      )
    );
  }

  //Listar
  public async listar(): Promise<void> {
    this.gateways$?.unsubscribe();
    this.gateways$ = this.lista2.gateways(this.query, 30).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.gateways = data.datos;
    });
  }

  // Acciones
  // Crear y editar se abren en modal; al cerrar con resultado se refresca
  public crear() {
    this.abrirFormulario();
  }

  public editar(gateway: IGateway) {
    this.abrirFormulario(gateway);
  }

  private abrirFormulario(gateway?: IGateway) {
    this.formularios
      .abrir(CrearEditarGatewayComponent, {
        tamano: 'mediano',
        data: gateway ? { edit: gateway } : undefined,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listar();
      });
  }

  public verDetalles(gateway: IGateway): void {
    this.navigator.navigate([
      'main/administracion/gateways/detalles',
      gateway._id,
    ]);
  }

  public async eliminar(gateway: IGateway) {
    const res = await this.dialogService.confirm(
      'Eliminar gateway',
      `¿Querés eliminar el gateway ${gateway.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.deleteById(gateway._id!);
      this.helper.notifSuccess('Gateway eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IGateway>[] = [
      // GatewayEUI
      {
        header: {
          label: 'Gateway EUI',
          sortable: true,
          sort: 'gatewayEui',
        },
        row: {
          html: (dato) =>
            dato.gatewayEui
              ? `<span class="tabla-codigo">${escaparHtml(dato.gatewayEui)}</span>`
              : '',
          noData: 'Sin EUI',
        },
      },
      // Nombre
      {
        header: {
          label: 'Nombre',
          sortable: true,
        },
        row: {
          field: 'nombre',
          noData: 'Sin nombre',
        },
      },

      // Fecha Creación
      {
        header: {
          label: 'Fecha de creación',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
        },
      },
      // ToA Promedio (última hora)
      {
        header: {
          label: 'ToA promedio',
          sortable: true,
          sort: 'toaPromedioHora',
        },
        row: {
          parse(dato) {
            return dato.toaPromedioHora != null
              ? dato.toaPromedioHora.toLocaleString('es-AR')
              : undefined;
          },
          suffix: ' ms',
          noData: 'Sin ToA',
        },
      },
      // Salud del gateway, según la ocupación del canal (ToA)
      {
        header: {
          label: 'Salud',
          sortable: true,
          sort: 'estadoSalud',
        },
        row: {
          estado: (dato) => this.estadoSalud(dato),
          noData: 'Sin estado de salud',
        },
      },
      // Envío de stats (degradación): recibidos vs esperados en la última franja
      {
        header: {
          label: 'Envío de stats',
        },
        row: {
          estado: (dato) => this.estadoStats(dato),
          noData: 'Sin stats esperados',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }
    // Acciones: pocas y sueltas (sin menú); Eliminar al final
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.verDetalles(dato),
            disabled: () =>
              !this.helper.tienePermiso('Administración - Ver gateways'),
          },
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Administración - Editar gateways'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Administración - Eliminar gateways'),
          },
        ],
      },
    });
    this.columnas = columnas;
  }

  /** Salud según la ocupación del canal (ToA): ok menos de 5 %, warning
   *  entre 5 y 17 %, error más de 17 %. Antes se mostraba la ocupación
   *  (Óptimo / Moderado / Alto); ahora se nombra la salud. */
  private estadoSalud(dato: IGateway): IEstadoCelda | undefined {
    const tooltip =
      'Según la ocupación del canal (ToA): buena con menos del 5 %, regular entre 5 y 17 %, baja con más del 17 %';
    switch (dato.estadoSalud) {
      case 'ok':
        return { texto: 'Buena', tono: 'ok', icono: 'check_circle', tooltip };
      case 'warning':
        return { texto: 'Regular', tono: 'aviso', icono: 'warning', tooltip };
      case 'error':
        return { texto: 'Baja', tono: 'error', icono: 'error', tooltip };
      default:
        return undefined;
    }
  }

  /** Stats recibidos vs esperados en la última franja (ratio derivado de los
   *  crudos: el gateway ya no guarda estado ni ratio). */
  private estadoStats(dato: IGateway): IEstadoCelda | undefined {
    const rec = dato.statsRecibidos;
    const esp = dato.statsEsperados;
    if (rec == null || esp == null || esp <= 0) return undefined;
    const ratio = Math.min(1, rec / esp);
    const detalle = `${Math.round(ratio * 100)} %`;
    const tooltip = `${rec} de ${esp} stats recibidos`;
    if (rec === 0) {
      return {
        texto: 'Sin stats',
        tono: 'neutro',
        icono: 'cloud_off',
        detalle,
        tooltip,
      };
    }
    if (ratio >= 0.9) {
      return {
        texto: 'Óptimo',
        tono: 'ok',
        icono: 'check_circle',
        detalle,
        tooltip,
      };
    }
    if (ratio >= 0.5) {
      return {
        texto: 'Degradado',
        tono: 'aviso',
        icono: 'warning',
        detalle,
        tooltip,
      };
    }
    return {
      texto: 'Crítico',
      tono: 'error',
      icono: 'error',
      detalle,
      tooltip,
    };
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.gateways$?.unsubscribe();
  }
}
